import * as Sentry from "@sentry/react";
import Hls, { errorData } from 'hls.js';
import { useMemo, useState } from 'react';
import { VideoTexture } from 'three';
import { addMediaElementToAudioStreamDestination } from '../utils/audio-manager';
import { ManagedBufferSource } from '../utils/ManagedBufferSource';
// import { logAllVideoEvents } from '@/utils/media';

interface IVideoConfig {
	muted?: boolean;
	loop?: boolean;
	autoplay?: boolean;
}

export interface IHlsPreloadData extends IVideoConfig {
	url: string;
	id: string;
}

type IHlsTextureData = [VideoTexture | undefined, HTMLVideoElement | undefined, Hls | undefined, ManagedBufferSource | null];
interface IExtension extends IVideoConfig {
	// onWaiting?: (isWaiting: boolean, e: Event) => any;
	// onEnded?: (video: HTMLVideoElement, e: Event) => any;
	onError?: () => unknown;
	// onCanPlay?: () => any;
	errorMessage?: string;
	preventVideoLoad?: boolean;
	audioContext?: AudioContext;
	audioStreamDestination?: MediaStreamAudioDestinationNode;
	onLoadedData?: () => unknown;
	addVideoToDom?: boolean;
	separateAudioFromVideo?: boolean;
}

export const autoplayableVideos: HTMLVideoElement[] = [];

export const createAutoplayVideoNodes = (videoCount: number = 10): void => {
	for (let index = 0; index < videoCount; index++) {
		const video = document.createElement('video');
		video.setAttribute('playsinline', 'true');
		video.setAttribute('webkit-playsinline', 'true');
		video.setAttribute('crossorigin', 'anonymous');
		video.crossOrigin = 'anonymous';
		video.muted = true;
		autoplayableVideos.push(video);
	}
};

// TODO: should this be changed?
export const unmuteAutoplayVideos = (): void => {
	autoplayableVideos.forEach((video) => (video.muted = false));
};

// cache for texture data
export const hlsVideoCache: { [id: string]: IHlsTextureData } = {};

// What each cached entry was actually built from. The cache is keyed by entity id alone, and an entity
// keeps its id when its media file is swapped — so without this, a swapped video kept playing the old
// one forever. Kept as a side table rather than folded into `IHlsTextureData` because that tuple is
// consumed positionally all over the component tree.
const hlsVideoSourceById: { [id: string]: { url: string; loop: boolean; autoplay: boolean } } = {};

// Teardown for the separate-audio sync rig (the 200ms interval plus the video listeners that drive it).
// Kept separate from the build teardown below because it is re-run on *every* source swap: a rig is bound
// to one source's `audio.m4a`, while the element, the Hls instance and their listeners live on.
const audioRigTeardownById: { [id: string]: () => void } = {};

const runAudioRigTeardown = (id: string): void => {
	try {
		audioRigTeardownById[id]?.();
	} catch (error) {
		console.warn('Error tearing down audio sync rig: ', error);
	}
	delete audioRigTeardownById[id];
};

// Teardown belonging to the build as a whole — the `abandoned` guard and our hls.js ERROR listener. Run
// only on eviction, never on a swap, since a swap deliberately keeps the Hls instance alive.
const buildTeardownById: { [id: string]: (() => void)[] } = {};

const registerBuildTeardown = (id: string, teardown: () => void): void => {
	if (!buildTeardownById[id]) buildTeardownById[id] = [];
	buildTeardownById[id].push(teardown);
};

// Bumped on every in-place source swap. Now that the element is reused, element identity alone no longer
// tells two builds apart, so an audio rig whose `createAudioBuffer` fetch outlived its swap needs this to
// know it has been superseded.
const entrySwapCountById: { [id: string]: number } = {};

let hlsVideoCacheHasBeenUnmuted = false;

export const unmuteVideosInCache = (): void => {
	if (!hlsVideoCacheHasBeenUnmuted) {
		hlsVideoCacheHasBeenUnmuted = true;
		for (const id in hlsVideoCache) {
			// If there is an audiobuffer it means video audio is managed separately from the video.
			// The video remains always muted and we only want to unmute the separate audio buffer.
			const audioBuffer = hlsVideoCache[id][3];
			if (audioBuffer) {
				audioBuffer.muted = false;
				continue;
			}
			const video = hlsVideoCache[id][1];
			if (video) video.muted = false;
		}
	}
};

const syncAudio = (videoEl: HTMLVideoElement, bufferSourceNode: ManagedBufferSource) => {
	try {
		if (!videoEl.paused && bufferSourceNode.paused) {
			bufferSourceNode.play(videoEl.currentTime);
		} else if (videoEl.paused && !bufferSourceNode.paused) {
			bufferSourceNode.stop();
		}

		// Sync timing if they drift apart (only when both are playing)
		if (!videoEl.paused && !bufferSourceNode.paused) {
			const timeDiff = Math.abs(bufferSourceNode.currentTime - videoEl.currentTime);
			if (timeDiff > 0.3) {
				// Allow 300ms drift before resync
				// console.log(`🔄 Resyncing audio: diff=${timeDiff.toFixed(2)}s; videoTime: ${videoEl.currentTime}, audioTime: ${bufferSourceNode.currentTime}`);
				bufferSourceNode.play(videoEl.currentTime);
			}
		}
	} catch (error) {
		console.warn('Sync error:', error);
	}
};

const createAudioBuffer = async (videoUrl: string, audioContext: AudioContext): Promise<AudioBuffer | null> => {
	const baseUrl = videoUrl.replace(/\/[^\/]*\.m3u8.*$/, '');
	const audioUrl = `${baseUrl}/audio.m4a`;
	let audioBuffer: AudioBuffer | null = null;
	try {
		const response = await fetch(audioUrl);
		const arrayBuffer = await response.arrayBuffer();
		audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
	} catch (error) {
		Sentry.captureException(error);
		console.warn('Error loading audio data: returning null for audio buffer', error);
	}

	return audioBuffer;
};

// cache for texture errors
const errorCache: { [id: string]: Error } = {};

// A separate-audio sync rig: the buffer source driving the audio, paired with the teardown that unwinds
// the interval and video listeners keeping it in step.
type AudioSyncRig = [ManagedBufferSource | null, () => void];

const noopTeardown = (): void => {};

/**
 * Build the rig that keeps a separately-managed audio track in step with a video element.
 *
 * Module scope rather than a render-body closure so the in-place swap path can rebuild the rig for a new
 * source without going through a full entry rebuild — hence `video` and `url` as explicit parameters.
 *
 * `canConnectAudioGraph` must be false on a swap: the no-audio-buffer fallback below routes the element
 * into the audio graph via `createMediaElementSource`, which throws `InvalidStateError` if it ever runs
 * twice on the same element. The routing from the original build is still live and still applies to the
 * new source, so re-running it is both illegal and unnecessary.
 */
const setupSeparateAudioSync = async (
	video: HTMLVideoElement,
	url: string,
	ext: IExtension | undefined,
	canConnectAudioGraph: boolean
): Promise<AudioSyncRig> => {
	if (!ext?.audioContext || !ext?.audioStreamDestination) return [null, noopTeardown];
	let syncInterval = 0;
	let videoReady = false;
	let hasStartedAudio = false;
	video.muted = true;
	const audioBuffer = await createAudioBuffer(url, ext.audioContext);
	if (!audioBuffer) {
		// If no audio buffer, fallback to add video directly to audio context as separate management of audio not needed
		if (canConnectAudioGraph) addMediaElementToAudioStreamDestination(ext.audioContext, ext.audioStreamDestination, video);
		return [null, noopTeardown];
	}

	const managedBufferSource = new ManagedBufferSource(ext.audioContext, ext.audioStreamDestination, audioBuffer);

	const playAudio = () => {
		try {
			if (!hasStartedAudio) {
				hasStartedAudio = true;
				managedBufferSource.play(video.currentTime);
			}
		} catch (error) {
			console.warn('Play handler error:', error);
		}
		// Continuous sync monitoring with error handling
		if (syncInterval === 0) syncInterval = window.setInterval(() => syncAudio(video, managedBufferSource), 200);
	}

	const onCanPlay = () => {
		videoReady = true;
		playAudio();
	};

	const onPlay = () => {
		if (!videoReady) {
			console.warn('Video not ready yet; delaying audio sync');
			return;
		}
		playAudio();
	};

	const onPause = () => {
		try {
			managedBufferSource.stop();
		} catch (error) {
			console.warn('Pause handler error:', error);
		}
		clearInterval(syncInterval);
		syncInterval = 0;
	};

	const onSeeked = () => {
		try {
			managedBufferSource.play(video.currentTime);
		} catch (error) {
			console.warn('Seek handler error:', error);
		}
	};

	const onVolumeChange = () => {
		video.muted = true;
	};

	video.addEventListener('canplay', onCanPlay);
	video.addEventListener('play', onPlay);
	video.addEventListener('pause', onPause);
	video.addEventListener('seeked', onSeeked);
	video.addEventListener('volumechange', onVolumeChange);

	// The `pause` handler alone cannot be trusted to clear the interval: pausing an already-paused
	// element fires no event, so a video that buffered but never played would leave the 200ms interval
	// running forever, pinning both the video element and this buffer source.
	const teardown = () => {
		clearInterval(syncInterval);
		syncInterval = 0;
		video.removeEventListener('canplay', onCanPlay);
		video.removeEventListener('play', onPlay);
		video.removeEventListener('pause', onPause);
		video.removeEventListener('seeked', onSeeked);
		video.removeEventListener('volumechange', onVolumeChange);
	};

	return [managedBufferSource, teardown];
};

/**
 * Attach a finished sync rig to the entry it was built for, or throw it away if that entry has since been
 * swapped, rebuilt or evicted.
 *
 * `createAudioBuffer` fetches and decodes, so a source swap can land while this is still in flight.
 * Testing only whether slot 3 is empty does not catch that — a freshly swapped entry has a null slot 3,
 * so a stale rig reads it as vacant and claims it, leaving the previous clip's soundtrack playing over
 * the new video. The element identity check covers a full rebuild; `swapCount` covers an in-place swap,
 * where the element deliberately stays the same.
 */
const commitAudioSync = (id: string, video: HTMLVideoElement, swapCount: number, [bufferSource, teardown]: AudioSyncRig): void => {
	const cached = hlsVideoCache[id];
	if (cached?.[1] === video && (entrySwapCountById[id] ?? 0) === swapCount && !cached[3]) {
		cached[3] = bufferSource;
		audioRigTeardownById[id] = teardown;
		return;
	}
	teardown();
	bufferSource?.destroy();
};

/**
 * Repoint a cached entry at a new media file, reusing its video element and VideoTexture.
 *
 * A live content update can swap an entity's media file while keeping its id. Rebuilding the entry works
 * but throws the element away, and that element cannot be replaced: `autoplayableVideos` supplies
 * elements that have been played-and-paused so a later `play()` resolves inside a user gesture (Safari's
 * multi-soundtrack rule), the pool is sized from the content doc with no margin, and `Background360`
 * draws from it uncounted — so it is routinely empty by the time a swap happens, and a rebuilt autoplay
 * video silently loses the ability to start. Elements keep that blessing across an `src` change, so
 * reusing one sidesteps the pool entirely.
 *
 * Keeping the VideoTexture also means the three.js material never rebinds, so the swap has no reload
 * flash — the texture goes on presenting the last decoded frame until the new source's first frame lands.
 */
const swapEntrySource = (id: string, url: string, ext: IExtension | undefined): void => {
	const cached = hlsVideoCache[id];
	if (!cached) return;
	const [, video, hls] = cached;
	if (!video) return;

	const swapCount = (entrySwapCountById[id] = (entrySwapCountById[id] ?? 0) + 1);

	// The rig is bound to the previous source's `audio.m4a`, so it always goes. Slot 3 is cleared so the
	// replacement rig's commit finds a vacant slot.
	runAudioRigTeardown(id);
	cached[3]?.destroy();
	cached[3] = null;

	// A failure recorded against the previous URL must not make the new one throw on its next render.
	delete errorCache[id];

	// Resume afterwards if it was playing: `load()` and a detach/attach cycle both pause the element, and
	// a live edit should not silently stop a video that was running.
	const wasPlaying = !video.paused;

	if (hls) {
		// Driven manually rather than leaning on `loadSource` alone, because the two pinned versions
		// disagree: 1.6.15 (Designer) rebuilds the MediaSource internally on a URL change, while 0.14.17
		// (JUTE) only sets the URL and fires MANIFEST_LOADING, leaving the old MediaSource and its
		// SourceBuffers in place. This sequence is correct on both — 1.6.15 skips its own detach/attach
		// branch because the media is already detached by the time `loadSource` runs.
		//
		// No explicit `startLoad()`: the instance was built with `autoStartLoad: true`, so segment loading
		// restarts from `onManifestParsed`. The order matters for that — attaching *after* `loadSource`
		// means the manifest (a network round trip) lands with the media already attached, whereas
		// attaching first would let the stream controller restart against the previous source's levels.
		hls.stopLoad();
		hls.detachMedia();
		hls.loadSource(url);
		hls.attachMedia(video);
	} else {
		video.src = url;
		video.load();
	}

	if (ext?.separateAudioFromVideo) {
		void setupSeparateAudioSync(video, url, ext, false).then((rig) => commitAudioSync(id, video, swapCount, rig));
	}
	// Deliberately no `addMediaElementToAudioStreamDestination` for the non-separate case: the element was
	// wired into the audio graph on its original build and that routing still applies to the new source.

	if (wasPlaying) {
		video.addEventListener('loadeddata', () => {
			// Can reject if another load interrupts this one — harmless, and not worth surfacing.
			video.play().catch(() => undefined);
		}, { once: true });
	}
};

export const useHlsLoader = (url: string, id: string, ext?: IExtension): IHlsTextureData => {
	const [_, triggerRender] = useState(0);
	// if video loading prevented return early
	if (ext?.preventVideoLoad) return [undefined, undefined, undefined, null];

	// if async error throw error into render phase (triggered via triggerRender)
	if (errorCache[id]) {
		const error = errorCache[id];
		// delete errorCache[id]; // leads to re-render issue if not commented out
		throw error;
	}

	const {
		errorMessage = 'failed to load video',
		loop = false,
		// muted = false,
		autoplay = false,
		onLoadedData,
	} = ext || {};

	// use cached data if exists otherwise throw promise
	if (hlsVideoCache[id]) {
		const source = hlsVideoSourceById[id];

		if (source) {
			// A live content update can swap an entity's media file while keeping its id. This used to
			// return the cached entry without ever comparing `url`, so the old video went on playing — the
			// URL was recomputed correctly upstream and then thrown away here. Swapped in place rather
			// than rebuilt, so the element keeps its playback blessing and the texture stays bound; see
			// `swapEntrySource`. No suspend: the old frame simply holds until the new source decodes.
			if (source.url !== url) {
				swapEntrySource(id, url, ext);
				source.url = url;
			}

			// `loop` and `autoplay` were honoured only at construction, so toggling either on an existing
			// video did nothing. Re-apply them — but only on an actual change, never on every cache hit:
			// this runs on every render, and blanket-reapplying the construction rules would re-mute every
			// video the moment after `unmuteVideosInCache` unmuted it on user interaction.
			const video = hlsVideoCache[id][1];
			if (video && source.loop !== loop) video.loop = loop;
			// Mirrors the construction rule below (`if (!autoplay) video.muted = true`), which derives
			// muting from autoplay rather than from `ext.muted`.
			if (video && source.autoplay !== autoplay && !autoplay) video.muted = true;
			source.loop = loop;
			source.autoplay = autoplay;
		}

		// restart loading when cache accessed if autoplay enabled & hls exists
		if (ext?.autoplay) {
			// if (cache[id][1]) cache[id][1]!.autoplay = true;
			hlsVideoCache[id][2]?.startLoad();
		}
		return hlsVideoCache[id];
	}

	// set up video
	let video: HTMLVideoElement;
	if (autoplay && autoplayableVideos.length) {
		video = autoplayableVideos[autoplayableVideos.length - 1];
		autoplayableVideos.splice(-1);
	} else {
		video = document.createElement('video');
	}

	// logAllVideoEvents(video)
	
	video.id = id;
	video.crossOrigin = 'anonymous';
	video.setAttribute('playsinline', 'true');
	video.setAttribute('webkit-playsinline', 'true');

	if (ext?.addVideoToDom) {
		video.style.width = '1px';
		video.style.height = '1px';
		video.style.position = 'absolute';
		video.style.zIndex = '-99999';
		document.body.appendChild(video);
	}

	const setSize = () => {
		video.width = video.videoWidth;
		video.height = video.videoHeight;
	};
	video.addEventListener('loadeddata', setSize);
	video.addEventListener('loadedmetadata', setSize);
	video.addEventListener('canplaythrough', setSize);

	if (onLoadedData) video.addEventListener('loadeddata', onLoadedData);

	// if (autoplay) video.autoplay = autoplay;
	if (loop) video.loop = loop;
	if (!autoplay) video.muted = true;

	// Flipped once this build is torn down, so its dying event handlers cannot report an error against the
	// entry that replaced it. Two ways that happens on a media swap:
	//
	// - hls.js 0.14.17 (JUTE) calls `removeAllListeners()` *last* in `destroy()`, after tearing its
	//   components down, so a fatal error escalated during teardown still reaches our handler. 1.6.15
	//   (Designer) removes listeners before that, hence the version-specific exposure.
	// - `video.load()` in the teardown below can fire `error` on the element being discarded, which the
	//   native/no-HLS branches route straight here.
	//
	// Either would leave a poisoned `errorCache[id]` that the *new* entry throws on at its next render,
	// and fire `ext.onError()` at the app for a video that failed on purpose.
	let abandoned = false;

	const cacheError = (data?: errorData) => {
		if (abandoned) return;
		ext?.onError?.();
		if (data) console.error('hls error: ', data);
		errorCache[id] = new Error(`hls error: ${data?.details || errorMessage}`);
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(errorCache[id] as any).hlsError = data;
		triggerRender((prevState) => prevState + 1);
	};

	// throw promise for suspense to catch
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	throw new Promise<any>((res) => {
		// logAllVideoEvents(video);
		let hls: Hls | undefined = undefined;
		let audioBufferSource: ManagedBufferSource | null = null;

		// The swap count this build starts life at, so a rig that resolves after a later swap is rejected.
		const buildSwapCount = entrySwapCountById[id] ?? 0;

		// Registered for every branch, not just the HLS one: the native and no-HLS paths below wire the
		// element's own `error` event to `cacheError`, and the teardown's `video.load()` can fire it.
		registerBuildTeardown(id, () => {
			abandoned = true;
		});

		if (Hls.isSupported() && url.endsWith('m3u8')) {
			hls = new Hls({
				autoStartLoad: true,
				// Enable audio support in HLS.js
				enableWorker: true,
			});
			hls.startLevel = -1; // automatic start level selection

			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const onHlsError = (e: string, data: any) => {
				if (!data.fatal) return;
				cacheError(data);
			};
			hls.on(Hls.Events.ERROR, onHlsError);

			// Unhook our own listener before `destroy()` runs, so teardown noise never reaches the app.
			// Deliberately targeted: the internal controllers register on this same emitter
			// (`event-handler.ts` -> `hls.on(...)`), so `removeAllListeners()` here would strip
			// buffer-controller's MEDIA_DETACHING handler and leak the MediaSource object URL.
			registerBuildTeardown(id, () => hls?.off(Hls.Events.ERROR, onHlsError));

			hls.once(Hls.Events.MEDIA_ATTACHED, async (_name, { media }) => {
				if (!ext?.audioContext || !ext?.audioStreamDestination) return;
				if (ext?.separateAudioFromVideo) {
					commitAudioSync(id, video, buildSwapCount, await setupSeparateAudioSync(video, url, ext, true));
				} else {
					addMediaElementToAudioStreamDestination(ext?.audioContext, ext?.audioStreamDestination, media);
				}
			});
			hls.attachMedia(video);
			hls.loadSource(url);
			// If HLS lib not supported (iOS safari) and separate audio required, set up separate audio sync without hls.js
	 	} else if (ext?.separateAudioFromVideo && url.endsWith('m3u8')) {
			video.src = url;
			void setupSeparateAudioSync(video, url, ext, true).then((rig) => commitAudioSync(id, video, buildSwapCount, rig));
			video.addEventListener('error', () => cacheError());
			// If not an HLS url, use native html element without HLS
		} else {
			video.src = url;
			if (ext?.audioContext && ext?.audioStreamDestination) addMediaElementToAudioStreamDestination(ext?.audioContext, ext?.audioStreamDestination, video);
			video.addEventListener('error', () => cacheError());
		}

		// We need to 'preload' videos until they can play so that "video.play().then(() => video.muted = false)" which
		// runs later on on user interaction and allows to run multiple soundtracks (audio/video) simultaneously works reliably.
		// If videos aren't preloaded video.play() doesn't resolve immediately (async) thus unmuting the video is outside the user interaction flow
		// and violating the safari multiple soundtrack permissions. Autoplay videos have already been preloaded when the video nodes were created and need to be ommitted in
		// the preloading to avoid video sounds becoming audible for a split second (autoplay videos are unmuted on launch)
		try {
			if (!autoplay) video.play().then(() => video.pause());
		} catch (error) {
			console.warn(error);
		}
		
		const videoTexture = new VideoTexture(video);

		hlsVideoCache[id] = [videoTexture, video, hls, audioBufferSource];
		// Record what this entry was built from so a later render can tell a media swap from an unrelated
		// property edit.
		hlsVideoSourceById[id] = { url, loop, autoplay };

		// resolve promise when video playable, using loadedmetadata as canplay
		// event not fired in safari
		video.addEventListener('loadedmetadata', res);
	});
};

// access cached hls data
export const useGetHlsCache = (id: string): IHlsTextureData => useMemo(() => hlsVideoCache[id], [id]);

// remove cached hls data
export const useRemoveHlsCache = (id: string): void =>
	// Delegates so it cannot drift from the teardown below and leave a stale `hlsVideoSourceById` record
	// behind, which would make a subsequent media swap look like a no-op.
	useMemo(() => clearHlsCacheById(id), [id]);

/**
 * Evict a cached video entry, releasing everything it owns.
 *
 * This used to only `delete` the map entry. Nothing called it, so nothing leaked — but it is now the
 * teardown path for a live media swap, and dropping the reference alone would strand a VideoTexture, an
 * HTMLVideoElement (still downloading and decoding) and an Hls instance (still fetching segments) per
 * edit. Only ever call this when the entry is genuinely being replaced or discarded: disposing an entry
 * still in use kills playback.
 */
export const clearHlsCacheById = (id: string): void => {
	// Before anything else: mark the build abandoned, unhook our hls.js ERROR listener, stop the sync
	// interval and unbind the video listeners — so nothing below can fire a handler against a destroyed
	// buffer source, a mid-reset video element, or the entry that is about to replace this one. Runs
	// outside the `cached` branch so these still unwind if the tuple was dropped by some other path.
	runAudioRigTeardown(id);
	buildTeardownById[id]?.forEach((teardown) => {
		try {
			teardown();
		} catch (error) {
			console.warn('Error running cached video teardown: ', error);
		}
	});
	delete buildTeardownById[id];
	delete entrySwapCountById[id];

	const cached = hlsVideoCache[id];
	if (cached) {
		const [videoTexture, video, hls, audioBufferSource] = cached;
		try {
			hls?.destroy();
			if (video) {
				video.pause();
				video.removeAttribute('src');
				video.load();
				video.remove();
			}
			// `destroy()`, not `dispose()` — the latter leaves the audio playing to the end of its buffer
			// and strands the gain node. The rig's pause handler is already unbound, so nothing else stops it.
			audioBufferSource?.destroy();
			videoTexture?.dispose();
		} catch (error) {
			console.warn('Error tearing down cached video: ', error);
		}
		delete hlsVideoCache[id];
	}
	if (hlsVideoSourceById[id]) delete hlsVideoSourceById[id];
	if (errorCache[id]) delete errorCache[id];
};


// export const useHlsPreloader = (hlsPreloadData: IHlsPreloadData[]): Promise<any> => {
// 	let promises: Promise<any>[] = [];
// 	for (let i = 0; i < hlsPreloadData.length; i++) {
// 		const { url, id, muted, loop, autoplay} = hlsPreloadData[i];

// 		let video: HTMLVideoElement;
// 		if (autoplay && autoplayableVideos.length) {
// 			video = autoplayableVideos[autoplayableVideos.length - 1];
// 			autoplayableVideos.splice(-1)
// 		} else {
// 			video = document.createElement('video')
// 		}

// 		video.id = id;
// 		video.crossOrigin = 'anonymous';
// 		video.setAttribute('playsinline', 'true');
// 		video.setAttribute('webkit-playsinline', 'true');
// 		//if (autoplay) video.autoplay = autoplay;
// 		if (loop) video.loop = true;
// 		// if (muted) video.muted = true;

// 		promises.push(new Promise<any>(res => {
// 			let hls: Hls | undefined = undefined;
// 			if (Hls.isSupported()) {
// 				hls = new Hls({ autoStartLoad: true });
// 				hls.startLevel = -1; // automatic start level selection
// 				hls.attachMedia(video);
// 				hls.loadSource(url);
// 				hls.on(Hls.Events.ERROR, (e, data) => {
// 					// // populate error cache and resolve
// 					if (!data.fatal) return;
// 					if (data) console.error('hls error: ', data)
// 					errorCache[id] = new Error(`hls error: ${data?.details || 'failed to load video'}`);
// 					(errorCache[id] as any).hlsError = data;
// 					res(true); // resolves as error saved in cache
// 				});
// 			} else if (video.canPlayType('application/vnd.apple.mpegurl')) {
// 				console.info('hls not supported: using only video element');
// 				// populate error cache and resolve
// 				video.src = url;
// 				video.addEventListener('error', () => {
// 					errorCache[id] = new Error('failed to load video');
// 					res(true) // resolves as error saved in cache
// 				});
// 			}
// 			hlsVideoCache[id] = [
// 				new VideoTexture(video, undefined, undefined, undefined, undefined, undefined, RGBAFormat),
// 				video, 
// 				hls
// 			];

// 			// resolve promise when video playable, using loadedmetadata as canplay
// 			// event not fired in safari
// 			video.addEventListener('loadedmetadata', () => {
// 				hls?.stopLoad?.();
// 				res(true);
// 			});

// 		}));
// 	}
// 	return Promise.all(promises)
// }
